import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../../redux/store";

// Import slice e types - PATH RELATIVI
import { saveOrdineLavoro } from "../ordiniLavoroSlice";

import {
    OrdineLavoro,
    STATI_ORDINE_LAVORO,
    StatoOrdineLavoro,
} from "../ordiniLavoroTypes";

import { ErrorStatusTypes } from "components/common/errorsDeclarations";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { convertToDateTime } from "components/common/dateUtils/convertToDateTime";

// Import custom hooks e componenti
import useAutoSave from "components/common/formUtility/useAutoSave";
import AutoSaveIndicator from "components/common/formUtility/AutoSaveIndicator";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import { NumericFormat } from "react-number-format";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

// ============================================
// INTERFACES
// ============================================

interface OrdineLavoroFormProps {
    ordineLavoro: OrdineLavoro;
}

// ============================================
// COMPONENT
// ============================================

const OrdineLavoroForm = ({ ordineLavoro }: OrdineLavoroFormProps) => {
    const dispatch = useAppDispatch();

    // ============================================
    // REACT HOOK FORM SETUP
    // ============================================

    const {
        handleSubmit,
        register,
        formState: { errors, isDirty, isValid },
        setValue,
        watch,
        control,
        setError,
        clearErrors,
        reset,
    } = useForm<OrdineLavoro>({
        mode: "onChange",
        defaultValues: ordineLavoro,
    });

    // ============================================
    // SELECTORS
    // ============================================

    const ordineLavoroStatus = useSelector<RootState, ErrorStatusTypes>(
        (state) => state.ordiniLavoroState.ordiniLavoroList.errorsStack.status
    );

    // ============================================
    // GESTIONE ERRORI SERVER
    // ============================================

    const listaCampi = useMemo(
        () => Object.keys(ordineLavoro) as Array<keyof OrdineLavoro>,
        [ordineLavoro]
    );

    // Reset form quando cambiano i dati
    useEffect(() => {
        reset(ordineLavoro, { keepDirty: false });
    }, [ordineLavoro.id, reset]);

    // Gestione errori dal server sui campi
    useEffect(() => {
        clearErrors();
        listaCampi.forEach((field) => {
            if (ordineLavoro?.errorsStack?.fieldsErrors?.[field]) {
                setError(field, {
                    type: "server",
                    message: ordineLavoro.errorsStack.fieldsErrors[field].toString(),
                });
            }
        });
    }, [ordineLavoro?.errorsStack?.fieldsErrors, listaCampi, setError, clearErrors]);

    // ============================================
    // SUBMIT HANDLER
    // ============================================

    const onSubmit: SubmitHandler<OrdineLavoro> = (data) => {
        dispatch(saveOrdineLavoro({ ordineLavoroToSave: data }));
        reset(data);
    };

    // ============================================
    // AUTOSAVE HOOK
    // ============================================

    const autoSave = useAutoSave({
        watch,
        handleSubmit,
        isDirty,
        isValid,
        delay: 600,
        onSubmit,
        canSubmit: !!ordineLavoro.id,
    });

    // ============================================
    // OPTIONS STATI
    // ============================================

    const statiOptions = Object.entries(STATI_ORDINE_LAVORO).map(([key, value]) => ({
        key: key as StatoOrdineLavoro,
        value,
    }));

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                {/* AutoSave Indicator */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <AutoSaveIndicator
                        isDirty={isDirty}
                        status={ordineLavoroStatus}
                        variant="chip"
                        size="small"
                    />
                </Box>

                <Grid container spacing={2}>
                    {/* Stato */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Controller
                            name="stato"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    value={
                                        statiOptions.find((opt) => opt.key === field.value) || null
                                    }
                                    options={statiOptions}
                                    getOptionLabel={(option) => option?.value || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.key === value?.key
                                    }
                                    onChange={(_, newValue) => {
                                        const selectedKey = newValue?.key || "CONF";
                                        field.onChange(selectedKey);
                                        // AutoSave immediato
                                        if (ordineLavoro.id) {
                                            handleSubmit(onSubmit)();
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Stato"
                                            required
                                            error={!!errors?.stato}
                                            helperText={errors?.stato?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>

                    {/* Quantità prevista */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Controller
                            name="qta_prevista"
                            control={control}
                            render={({ field }) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Qtà Prevista"
                                    variant="outlined"
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={1}
                                    value={field.value || ""}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.qta_prevista}
                                    helperText={errors?.qta_prevista?.message}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* Quantità arrivata */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Controller
                            name="qta_arrivata"
                            control={control}
                            render={({ field }) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Qtà Arrivata"
                                    variant="outlined"
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={1}
                                    value={field.value || ""}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.qta_arrivata}
                                    helperText={errors?.qta_arrivata?.message}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* Spazio vuoto per allineamento */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} />

                    {/* Data arrivo prevista */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <DesktopDatePicker
                            label="Data Arrivo Prevista"
                            value={
                                watch("data_arrivo_prevista")
                                    ? convertToDateTime(watch("data_arrivo_prevista"))
                                    : null
                            }
                            onChange={(newValue) => {
                                setValue(
                                    "data_arrivo_prevista",
                                    newValue
                                        ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || null
                                        : null,
                                    { shouldDirty: true }
                                );
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors?.data_arrivo_prevista,
                                    helperText: errors?.data_arrivo_prevista?.message,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                    </Grid>
                    {/* Data ricevimento */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <DesktopDatePicker
                            label="Data Arrivo Effettivo"
                            value={
                                watch("data_ricevimento")
                                    ? convertToDateTime(watch("data_ricevimento"))
                                    : null
                            }
                            onChange={(newValue) => {
                                setValue(
                                    "data_ricevimento",
                                    newValue
                                        ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || null
                                        : null,
                                    { shouldDirty: true }
                                );
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors?.data_ricevimento,
                                    helperText: errors?.data_ricevimento?.message,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                    </Grid>
                    {/* Data consegna prevista */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <DesktopDatePicker
                            label="Data Consegna Prevista"
                            value={
                                watch("data_consegna_prevista")
                                    ? convertToDateTime(watch("data_consegna_prevista"))
                                    : null
                            }
                            onChange={(newValue) => {
                                setValue(
                                    "data_consegna_prevista",
                                    newValue
                                        ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd") || null
                                        : null,
                                    { shouldDirty: true }
                                );
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors?.data_consegna_prevista,
                                    helperText: errors?.data_consegna_prevista?.message,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                    </Grid>

                    {/* Spazio vuoto per allineamento */}
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} />

                    {/* Note */}
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Note"
                            variant="outlined"
                            slotProps={{
                                inputLabel: { shrink: true },
                            }}
                            multiline
                            rows={3}
                            {...register("note")}
                            error={!!errors?.note}
                            helperText={errors?.note?.message}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default OrdineLavoroForm;